import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { styled } from 'styled-components';

import { Gender, Locale } from '../../../../shared/api/hscan/account';
import { useUserState } from '../../../../shared/hooks/accountHook';
import {
  useUpdateUserAddress,
  useUserAddress,
} from '../../../../shared/hooks/addressHook';
import birthIcon from '../../assets/icons/icon_birth.svg';
import genderIcon from '../../assets/icons/icon_gender.svg';
import peopleIcon from '../../assets/icons/icon_people.svg';
import phoneIcon from '../../assets/icons/icon_phone.svg';
import usernameIcon from '../../assets/icons/icon_username.svg';
import { ColoredIcon } from '../../common/components/Icon';
import { AddressTextField } from '../../common/components/Slot/AddressTextField';
import {
  SlotGrid,
  SlotGridWrapper,
} from '../../common/components/Slot/SlotWrapper';
import { TextFieldSlot } from '../../common/components/Slot/TextFieldSlot';
import { Spacer } from '../../common/components/Spacer';

import { EditLocale } from './EditLocale';
import { EditTarget } from './MyPage';
import { EditButton } from './styledComponent';

const UserInfoSlotGrid = styled(SlotGrid)`
  row-gap: 24px;
`;

export interface UserInfoProps {
  onClickEditName: () => void;
  onClickEditPhone: () => void;
  onSuccessEdit: (target: EditTarget) => void;
}
export const UserInfo = (props: UserInfoProps) => {
  const { onClickEditName, onClickEditPhone, onSuccessEdit } = props;
  const { t } = useTranslation('translation', {
    keyPrefix: 'features.myPage',
  });
  const [user] = useUserState();
  const { data: address } = useUserAddress();
  const { mutate: updateAddress } = useUpdateUserAddress({
    onSuccess: () => {
      setEditingAddress(false);
      onSuccessEdit(EditTarget.Address);
    },
  });
  const [editingAddress, setEditingAddress] = useState(false);
  const getGenderString = (gender?: Gender) => {
    switch (gender) {
      case Gender.F:
        return t('gender.female');
      case Gender.M:
        return t('gender.male');
      case Gender.O:
        return t('gender.other');
      default:
        return '';
    }
  };

  const [locale, setLocale] = useState<Locale | null>(null);
  const [editingLocale, setEditingLocale] = useState(false);
  useEffect(() => {
    if (user) {
      setLocale(user.locale ?? null);
    }
  }, [user]);

  return (
    <SlotGridWrapper>
      <UserInfoSlotGrid $marginTop="47px" $width={450} $columnGap={56}>
        <TextFieldSlot
          label={`${t('slots.username.title')}:`}
          textAlign="center"
          value={user?.username ?? ''}
          icon={
            <ColoredIcon
              width="24px"
              height="24px"
              imageUrl={usernameIcon}
              imageWidth="20px"
              color={'var(--HScan_Light_Blue, #2e5be0)'}
            />
          }
          fullwidth
          readOnly>
          <Spacer width={'45px'} />
        </TextFieldSlot>
        <br />
        <TextFieldSlot
          label={`${t('slots.name.title')}:`}
          textAlign="center"
          value={user?.name ?? ''}
          icon={
            <ColoredIcon
              width="24px"
              height="24px"
              imageUrl={peopleIcon}
              imageWidth="18px"
              color={'var(--HScan_Light_Blue, #2e5be0)'}
            />
          }
          fullwidth
          readOnly>
          <EditButton onClick={onClickEditName} />
        </TextFieldSlot>
        <TextFieldSlot
          textAlign="center"
          label={`${t('slots.birth.title')}:`}
          value={user?.birthDate ?? ''}
          icon={
            <ColoredIcon
              width="24px"
              height="24px"
              imageUrl={birthIcon}
              imageWidth="24px"
              color={'var(--HScan_Light_Blue, #2e5be0)'}
            />
          }
          fullwidth
          readOnly>
          <Spacer width={'45px'} />
        </TextFieldSlot>
        <TextFieldSlot
          label={`${t('slots.gender.title')}:`}
          textAlign="center"
          value={getGenderString(user?.gender) ?? ''}
          icon={
            <ColoredIcon
              width="24px"
              height="24px"
              imageUrl={genderIcon}
              imageWidth="20px"
              color={'var(--HScan_Light_Blue, #2e5be0)'}
            />
          }
          fullwidth
          readOnly>
          <Spacer width={'45px'} />
        </TextFieldSlot>
        <TextFieldSlot
          label={`${t('slots.phoneNumber.title')}:`}
          textAlign="center"
          value={user?.telecom ?? ''}
          icon={
            <ColoredIcon
              width="24px"
              height="24px"
              imageUrl={phoneIcon}
              imageWidth="24px"
              color={'var(--HScan_Light_Blue, #2e5be0)'}
            />
          }
          fullwidth
          readOnly>
          <EditButton onClick={onClickEditPhone} />
        </TextFieldSlot>
        <AddressTextField
          value={address}
          label={`${t('slots.address.title')}:`}
          onEdit={() => setEditingAddress(true)}
          onSave={updateAddress}
          showSaveButton={editingAddress}
          readOnly={!editingAddress}
        />
        <EditLocale
          locale={locale}
          setLocale={setLocale}
          edit={editingLocale}
          onEdit={() => setEditingLocale(true)}
          onEditSuccess={() => {
            setEditingLocale(false);
            onSuccessEdit(EditTarget.Locale);
          }}
        />
      </UserInfoSlotGrid>
    </SlotGridWrapper>
  );
};
